/**
 * JoSmart Uni - Mode Hors-Ligne Amélioré
 * IndexedDB cache for GET responses + pending writes queue
 */

const DB_NAME = 'josmart_uni_offline';
const DB_VERSION = 1;
const CACHE_STORE = 'api_cache';
const PENDING_STORE = 'pending_requests';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

let db = null;

function openDB() {
  return new Promise((resolve, reject) => {
    if (db) { resolve(db); return; }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const database = e.target.result;
      if (!database.objectStoreNames.contains(CACHE_STORE)) {
        database.createObjectStore(CACHE_STORE, { keyPath: 'url' });
      }
      if (!database.objectStoreNames.contains(PENDING_STORE)) {
        database.createObjectStore(PENDING_STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    request.onsuccess = (e) => { db = e.target.result; resolve(db); };
    request.onerror = (e) => reject(e.target.error);
  });
}

// ============ CACHE (GET responses) ============

export async function getCachedResponse(url) {
  try {
    const database = await openDB();
    return new Promise((resolve) => {
      const tx = database.transaction(CACHE_STORE, 'readonly');
      const store = tx.objectStore(CACHE_STORE);
      const request = store.get(url);
      request.onsuccess = () => {
        const result = request.result;
        if (result && (Date.now() - result.timestamp) < CACHE_TTL) {
          resolve(result.data);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => resolve(null);
    });
  } catch { return null; }
}

export async function setCachedResponse(url, data) {
  try {
    const database = await openDB();
    const tx = database.transaction(CACHE_STORE, 'readwrite');
    const store = tx.objectStore(CACHE_STORE);
    store.put({ url, data, timestamp: Date.now() });
  } catch { /* silent */ }
}

export async function clearCache() {
  try {
    const database = await openDB();
    const tx = database.transaction(CACHE_STORE, 'readwrite');
    tx.objectStore(CACHE_STORE).clear();
  } catch { /* silent */ }
}

// ============ PENDING WRITES (offline queue) ============

export async function addPendingRequest(config) {
  try {
    const database = await openDB();
    const tx = database.transaction(PENDING_STORE, 'readwrite');
    const store = tx.objectStore(PENDING_STORE);
    store.add({
      method: config.method,
      url: config.url,
      data: config.data,
      headers: config.headers,
      timestamp: Date.now()
    });
  } catch { /* silent */ }
}

export async function getPendingRequests() {
  try {
    const database = await openDB();
    return new Promise((resolve) => {
      const tx = database.transaction(PENDING_STORE, 'readonly');
      const store = tx.objectStore(PENDING_STORE);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => resolve([]);
    });
  } catch { return []; }
}

export async function removePendingRequest(id) {
  try {
    const database = await openDB();
    const tx = database.transaction(PENDING_STORE, 'readwrite');
    tx.objectStore(PENDING_STORE).delete(id);
  } catch { /* silent */ }
}

export async function clearPendingRequests() {
  try {
    const database = await openDB();
    const tx = database.transaction(PENDING_STORE, 'readwrite');
    tx.objectStore(PENDING_STORE).clear();
  } catch { /* silent */ }
}

export async function getPendingCount() {
  const pending = await getPendingRequests();
  return pending.length;
}

// ============ PRELOAD BY ROLE ============

const ROLE_PRELOAD = {
  student: [
    '/api/gradebook/student/my-grades',
    '/api/assignments',
    '/api/my-assignment-grades',
    '/api/online-courses/my-classrooms',
    '/api/auth/me',
  ],
  teacher: [
    '/api/assignments',
    '/api/online-courses/my-classrooms',
    '/api/auth/me',
  ],
  visiting_teacher: [
    '/api/assignments',
    '/api/online-courses/my-classrooms',
    '/api/auth/me',
  ],
};

export async function preloadForRole(role, axiosInstance) {
  const urls = ROLE_PRELOAD[role] || ['/api/auth/me'];
  const baseURL = process.env.REACT_APP_BACKEND_URL;

  for (const path of urls) {
    try {
      const res = await axiosInstance.get(`${baseURL}${path}`);
      await setCachedResponse(path, res.data);
    } catch { /* silent - will use cache next time */ }
  }
}

// ============ SYNC ============

export async function syncOfflineData(axiosInstance) {
  const pending = await getPendingRequests();
  if (pending.length === 0) return { synced: 0, failed: 0 };

  let synced = 0;
  let failed = 0;

  for (const req of pending) {
    try {
      await axiosInstance({
        method: req.method,
        url: req.url,
        data: req.data,
        headers: req.headers
      });
      await removePendingRequest(req.id);
      synced++;
    } catch (err) {
      const status = err.response?.status;
      // Remove permanently failed requests (4xx errors except 429)
      if (status && status >= 400 && status < 500 && status !== 429) {
        await removePendingRequest(req.id);
      }
      failed++;
    }
  }

  return { synced, failed };
}
